import React, { PropsWithChildren, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Text } from 'components/vensaComponents';
import { Line } from 'PaymentsPage/components/Line';
import rtfToHtml from '@iarna/rtf-to-html';
import { useMyHealthStore } from 'store/mobx/mobx';
import styled from 'styled-components';
import Loading from 'components/loadingSpinner';

const DownloadLink = styled.a`
    font-size: var(--font-20);
    color: var(--color-pelorous);
`

export const DocumentDetailBox = observer(({ children }: PropsWithChildren<{}>) => (
  <Box
    bg="white"
    borderRadius="4px"
    boxShadow={6}
    p={3}
    direction="column"
    mb="16px"
    display="block"
  >
    {children}
  </Box>
));

export const DocumentDetailSectionHeader: React.FC<{
  itemName: string;
  givenName: string;
  familyName: string;
  organisationName: string;
  providerName: string;
}> = observer(
  ({ itemName, givenName, familyName, organisationName, providerName }) => (
    <Box justifyContent="center" py={"20px"}>
    <Box direction="column" alignItems="center" fontWeight={100}>
      <Text fontWeight={600} my="20px" fontSize={"19px"}>
        {itemName}
      </Text>
        <div>
          <Text textAlign={"center"} fontSize={"17px"}>
            {givenName} {familyName}
          </Text>
          <Text textAlign={"center"} fontSize={"15px"}>{providerName}</Text>
          <Text textAlign={"center"} fontSize={"15px"}>{organisationName}</Text>
        </div>
    </Box>
  </Box>
  ),
);

export const DocumentDetailSectionBottom: React.FC<{
  patientId: string,
  documentId: string,
  textContent: string;
  isPDF: boolean
  hasUrl: boolean
}> = observer(({ patientId, documentId, textContent, isPDF, hasUrl }) => {
  const [val, setVal] = useState("");
  const rtf = textContent.startsWith("{");
  const phrStore = useMyHealthStore();

  // Check if RTF format then convert to html, otherwise use the string given
  if (rtf) rtfToHtml.fromString(textContent, (err: any, html: any) => { setVal(html) })
  else if (textContent !== val) setVal(textContent);

  if (hasUrl) {
    return (
      <Box direction="column">
        <Text fontWeight={100} css={{whiteSpace: "normal", marginTop: "35px"}}>
        <Loading isFetching={phrStore.loading}/>
        <DownloadLink onClick={() => phrStore.downloadDocument(patientId, documentId)}>Click here to download document</DownloadLink>
      </Text>
    </Box>
    ) 
  }
  if (rtf) {
    return (
      <Box direction="column">
        <Text fontWeight={100} css={{whiteSpace:"normal"}}>
        {rtf && <div dangerouslySetInnerHTML={{__html: val}} />}
        {!rtf && textContent.split('\t').map(str => <p>{str}</p>)}
      </Text>
    </Box>
    )
  }  
  return null;
});

export const DocumentDetailSection: React.FC<{
  patientId: string;
  documentId: string;
  providerName: string;
  itemName: string;
  givenName: string;
  familyName: string;
  organisationName: string;
  textContent: string;
  isPDF: boolean
  hasUrl: boolean
}> = observer(
  ({
    patientId,
    documentId,
    providerName,
    itemName,
    givenName,
    familyName,
    organisationName,
    textContent,
    isPDF,
    hasUrl
  }) => {
    return (
      <DocumentDetailBox>
        <Box direction="column" pt={2}>
          <Text fontSize={"17px"} fontWeight={100}>Health Document</Text>
          <div style={{display: "flex", flexDirection: "row"}}>
            <Text
              display="flex"
              alignItems="start"
              justifyContent="end"
              direction="column"
              fontSize="11px"
              flexGrow={0}
            >
              <div style={{fontStyle: "italic", marginTop: "10px", marginBottom: "5px"}}>
                {organisationName}
              </div>
            </Text>
          </div>
          <Line my="10px" borderColor="#F3F3F3" />
        </Box>

        <DocumentDetailSectionHeader
          itemName={itemName}
          givenName={givenName}
          familyName={familyName}
          organisationName={organisationName}
          providerName={providerName}
        />

        <DocumentDetailSectionBottom
          patientId={patientId}
          documentId={documentId}
          textContent={textContent} 
          isPDF={isPDF}
          hasUrl={hasUrl}
        />
      </DocumentDetailBox>
    );
  },
);
